import { Play, Square } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { Button, Badge } from "@/components/ui/primitives";
import { useAutoRun } from "@/lib/playback";
import type { StageKey } from "@/components/StageStepper";

const STAGE_ORDER: StageKey[] = ["intake", "analysis", "coordination", "decision"];

const STAGE_LABEL: Record<StageKey, string> = {
  intake: "Intake",
  analysis: "Analysis",
  coordination: "Coordination",
  decision: "Decision",
};

export function AutoRunControl({
  appId,
  stage,
  status,
}: {
  appId: string;
  stage: StageKey;
  status: string;
}) {
  const { isAutoRunning, startAutoRun, stopAutoRun } = useAutoRun();
  const qc = useQueryClient();
  const running = isAutoRunning(appId);

  // Only show where auto-run makes sense: the app is still working its way
  // through the pipeline. Finalised apps and the Decision stage itself have
  // nothing left to auto-advance.
  const driveable =
    (status === "new" || status === "in_flight") && stage !== "decision";

  if (!driveable && !running) return null;

  if (running) {
    const idx = STAGE_ORDER.indexOf(stage);
    const total = STAGE_ORDER.length;
    return (
      <div className="flex items-center gap-3">
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            stopAutoRun();
          }}
          title="Stop auto-run"
        >
          <Square className="h-3.5 w-3.5" strokeWidth={2.4} />
          Stop
        </Button>
        <div className="flex items-center gap-2">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-warn/70" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-warn" />
          </span>
          <span className="text-xs font-medium text-warn">
            Auto-running · {STAGE_LABEL[stage]} {idx + 1} of {total}
          </span>
        </div>
      </div>
    );
  }

  return (
    <Button
      size="sm"
      variant="brand"
      title="Runs Intake → Analysis → Coordination → Decision automatically."
      onClick={() => {
        startAutoRun(appId);
        // Nudge queries so the stage pages pick up the auto-run state on
        // their next render cycle.
        qc.invalidateQueries({ queryKey: ["application", appId] });
      }}
    >
      <Play className="h-3.5 w-3.5" strokeWidth={2.4} />
      Run full journey
      <Badge variant="muted" className="ml-1 text-[0.62rem] uppercase">
        Auto
      </Badge>
    </Button>
  );
}
